import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

const STORAGE_KEY = 'onboarding_state';

interface OnboardingState {
  hasCompletedOnboarding: boolean;
  currentStep: number;
}

interface OnboardingContextType {
  hasCompletedOnboarding: boolean;
  showOnboarding: boolean;
  currentStep: number;
  totalSteps: number;
  startOnboarding: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipOnboarding: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const TOTAL_STEPS = 4;

function loadState(): OnboardingState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore parse errors
  }
  return { hasCompletedOnboarding: false, currentStep: 0 };
}

function saveState(state: OnboardingState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<OnboardingState>(loadState);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    const saved = loadState();
    // Show onboarding automatically for first-time users
    return !saved.hasCompletedOnboarding;
  });

  const startOnboarding = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: 0 }));
    setShowOnboarding(true);
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      const newStep = Math.min(prev.currentStep + 1, TOTAL_STEPS - 1);
      return { ...prev, currentStep: newStep };
    });
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => {
      const newStep = Math.max(prev.currentStep - 1, 0);
      return { ...prev, currentStep: newStep };
    });
  }, []);

  const skipOnboarding = useCallback(() => {
    const newState = { hasCompletedOnboarding: true, currentStep: 0 };
    setState(newState);
    saveState(newState);
    setShowOnboarding(false);
  }, []);

  const completeOnboarding = useCallback(() => {
    const newState = { hasCompletedOnboarding: true, currentStep: 0 };
    setState(newState);
    saveState(newState);
    setShowOnboarding(false);
  }, []);

  const resetOnboarding = useCallback(() => {
    const newState = { hasCompletedOnboarding: false, currentStep: 0 };
    setState(newState);
    saveState(newState);
    setShowOnboarding(true);
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        hasCompletedOnboarding: state.hasCompletedOnboarding,
        showOnboarding,
        currentStep: state.currentStep,
        totalSteps: TOTAL_STEPS,
        startOnboarding,
        nextStep,
        prevStep,
        skipOnboarding,
        completeOnboarding,
        resetOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};
